import {
    CloseOutlined,
    InfoOutlined as InfoOutlinedIcon,
    Menu as MenuIcon,
    PowerSettingsNew as PowerSettingsNewIcon,
    WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { Divider, Menu, MenuItem } from '@mui/material';
import { ReactComponent as SettingSvg } from 'assets/svg/Menu-Setting.svg';
import { ReactComponent as ClientPoolSvg } from 'assets/svg/Main/client-pool.svg';
import { ReactComponent as CompetitorChartSvg } from 'assets/svg/Main/TopTab-Competitor-Chart.svg';
import { ReactComponent as OverviewSvg } from 'assets/svg/Main/TopTab-Overview.svg';
import { ReactComponent as SaveListingSvg } from 'assets/svg/Main/TopTab-SaveListing-Heart.svg';
import { ReactComponent as CatastroSearchSvg } from 'assets/svg/CatastroSearch-Icon.svg';
import { TwoLetterAvatar } from 'components';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useOtpStore from 'store/useOtpStore';
import useUserStore from 'store/useUserStore';
import { ROUTES, SubscriptionPlans } from 'util/Constants';
import { GetTwoLetterName } from 'util/Funcs';
import { logoutUserAndClearLocalStorage } from 'util/localStorageHelper';
import { PlanTypes } from 'util/Types';
import HelloDataMark from '../HelloDataMark';
import styles from './main-header.module.css';
import NotificationArea from './NotificationArea';
import TopTabs from './TopTabs';
import { useAuthStore } from 'store/useAuthStore';
import { useToast } from 'components/Providers/ToastProvider';
import { useUtilsStore } from 'store/useUtilsStore';

// Add custom breakpoint for 900px
const customBreakpoint = '@media (min-width: 900px)';

const SettingAvatar = () => {
    const navigate = useNavigate();
    const { userInfo: user } = useUserStore();
    const { updateEmail } = useOtpStore();
    const { resendVerficationEmail } = useAuthStore();
    const { showMessage } = useToast();

    const { priceId, interval } = user?.subscriptionDetails || {
        interval: 'month',
        priceId: '',
    };

    const plan: PlanTypes = SubscriptionPlans.filter((p) => p.priceId[interval] === priceId)[0];

    const ref = useRef(null);

    const [expanded, setExpanded] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const userName = user?.fullName || '',
        email = user?.email || '';
    const twoLetterName = GetTwoLetterName(userName || '');
    const onboardingFinished = user?.accountType && user.subscriptionId;

    const onSettingClicked = () => navigate(ROUTES.SETTING);

    const onLogOutClicked = () => {
        logoutUserAndClearLocalStorage();
        updateEmail('');
        navigate(ROUTES.SIGN_IN);
    };

    const onReportProblemClicked = () => {
        window.open('https://gozenforms.com/6LBeTRxkF75Km2Ha2zjJ', '_blank');
    };

    const onResendVerificationClicked = async () => {
        try {
            await resendVerficationEmail();
            showMessage('Success', 'Verification email sent successfully');
        } catch (error: any) {
            showMessage('Error', error.message || 'Failed to send verification email');
        }
    };

    const openMenuClicked = (evt: any) => {
        setExpanded(true);
        evt.stopPropagation();
    };

    const closeMenuClicked = () => {
        setExpanded(false);
    };

    return (
        <div className="relative">
            <div className="cursor-pointer" onClick={openMenuClicked} title={userName} data-cy="setting-avatar">
                <TwoLetterAvatar title={twoLetterName} profileUrl={user?.profileImageUrl!} />
            </div>
            <Menu
                open={expanded}
                ref={ref}
                sx={{
                    mt: '60px',
                    borderRadius: '10px',
                    padding: '0px',
                    '& .MuiPaper-root': {
                        width: '303px',
                        borderRadius: '10px',
                    },
                    '& .MuiList-root': {
                        padding: '0px',
                    },
                }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                className="rounded-[10px] absolute"
                onClose={closeMenuClicked}
                anchorEl={anchorEl}
            >
                <MenuItem>
                    <div className="py-[10px] w-full text-left flex flex-col gap-[12px]">
                        <div className="text-black text-[18px] font-medium">{userName}</div>
                        <div className="text-[12px] text-opacity-60">{email}</div>
                        <div className="flex items-center gap-[10px]">
                            {plan && (
                                <>
                                    <img alt={`Plan Icon`} src={plan.icon} className="w-[18px] h-[18px]" />
                                    <span className="text-my-blue text-[12px] font-medium">
                                        {`${plan.title} ${user?.subscriptionDetails?.status === 'trialing' ? 'Trial' : 'Plan'}`}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </MenuItem>

                {onboardingFinished && (
                    <MenuItem onClick={onSettingClicked} className="h-[48px]">
                        <div className="flex gap-[10px] items-center">
                            <SettingSvg fontSize="small" />
                            <div className="text-[14px]" data-cy="setting-menu">
                                Settings
                            </div>
                        </div>
                    </MenuItem>
                )}
                {!user?.emailVerified && (
                    <>
                        <Divider />
                        <MenuItem className="h-[48px]">
                            <div className="flex gap-[10px] items-center">
                                <InfoOutlinedIcon fontSize="small" />
                                <div className="flex flex-col">
                                    <div className="text-red-500 text-[12px] font-medium">Email not verified</div>
                                    <button onClick={onResendVerificationClicked} className="text-my-blue text-[12px] hover:underline text-left">
                                        Send verification email
                                    </button>
                                </div>
                            </div>
                        </MenuItem>
                    </>
                )}
                <Divider />
                {/* Logout */}
                <MenuItem onClick={onLogOutClicked} className="!py-[20px]">
                    <div className="flex gap-[10px] items-center text-[14px]">
                        <PowerSettingsNewIcon fontSize="small" />
                        <span className="text-[14px]" data-cy="logout-menu">
                            Log out
                        </span>
                    </div>
                </MenuItem>
            </Menu>
        </div>
    );
};

const FeedbackButton = () => {
    const handleFeedbackClick = () => {
        window.open(
            'https://api.whatsapp.com/send?phone=34603400632&text=Hey%2C%20I%20would%20like%20to%20share%20some%20feedback%20for%20hello%3Adata',
            '_blank',
        );
    };

    return (
        <button
            onClick={handleFeedbackClick}
            className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-gray-100 transition-colors border border-gray-200"
            title="Share Feedback"
        >
            <WhatsAppIcon style={{ color: '#25D366' }} />
            <span className="hidden [@media(min-width:900px)]:inline text-[14px]">Feedback</span>
        </button>
    );
};

const MobileFeedbackButton = () => {
    const handleFeedbackClick = () => {
        window.open(
            'https://api.whatsapp.com/send?phone=34603400632&text=Hey%2C%20I%20would%20like%20to%20share%20some%20feedback%20for%20hello%3Adata',
            '_blank',
        );
    };

    return (
        <button
            onClick={handleFeedbackClick}
            className="flex items-center gap-3 w-full py-3 px-6 text-base hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
            title="Share Feedback"
        >
            <div className="w-6 h-6 flex items-center justify-center">
                <WhatsAppIcon style={{ color: '#25D366' }} />
            </div>
            <span>Feedback</span>
        </button>
    );
};

const ClickableHelloDataMark = () => {
    const navigate = useNavigate();
    const { setTopTabIndex } = useUtilsStore();

    const handleClick = () => {
        navigate(ROUTES.MAIN);
        setTopTabIndex(0);
    };

    return (
        <div onClick={handleClick} className="cursor-pointer">
            <HelloDataMark />
        </div>
    );
};

const MainHedaer = () => {
    const [open, setOpen] = useState(false);
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
    const { setTopTabIndex } = useUtilsStore();
    const { userInfo, isProUser } = useUserStore();
    const navigate = useNavigate();

    // Define all possible tab items (same as TopTabs component)
    const allTabItems = [
        { index: 0, label: 'Overview', icon: <OverviewSvg />, dataCy: 'overview-tab' },
        { index: 2, label: 'Saved Listings', icon: <SaveListingSvg />, dataCy: 'savelisting-tab' },
        { index: 1, label: 'Partners', icon: <CompetitorChartSvg />, dataCy: 'competitors-tab' },
        { index: 4, label: 'Buyer Pool', icon: <ClientPoolSvg />, dataCy: 'client-pool-tab' },
        { index: 6, label: 'Catastro Search', icon: <CatastroSearchSvg />, dataCy: 'catastro-search-tab' },
    ];

    // Filter tab items based on account type and plan
    const tabItems = allTabItems.filter((item) => {
        if (item.index === 4) {
            // Buyer Pool tab - only show for Company accounts
            return userInfo?.accountType === 'business';
        }
        if (item.index === 6) {
            // Catastro Search tab - only show for Pro users
            return isProUser();
        }
        return true;
    });

    const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMobileMenuAnchor(event.currentTarget);
        setOpen(true);
    };

    const handleMobileMenuClose = () => {
        setMobileMenuAnchor(null);
        setOpen(false);
    };

    const handleMobileTabClick = (index: number) => {
        navigate(ROUTES.MAIN);
        setTopTabIndex(index);
        handleMobileMenuClose();
    };

    return (
        <div className="border-b border-my-gray border-opacity-60 bg-white lg:px-0 px-[20px]">
            <div className={`${styles.laptopHeader} hidden md:block`}>
                <div className="h-[72px] lg:max-w-hello mx-auto flex items-center">
                    <div className="w-[200px]">
                        <ClickableHelloDataMark />
                    </div>
                    <div className="flex-1 flex justify-center">
                        <TopTabs />
                    </div>
                    <div className="w-[200px] flex justify-end items-center gap-[12px]">
                        <FeedbackButton />
                        {/* <NotificationArea /> */}
                        <SettingAvatar />
                    </div>
                </div>
            </div>

            {/* Mobile and Tablet Header with Burger Menu - shows on screens < 768px */}
            <div className="block md:hidden">
                <div className="flex px-4 py-3 h-fit items-center">
                    <div className="w-[40px]">
                        <div className="cursor-pointer p-2 hover:bg-gray-100 rounded-md transition-colors" onClick={handleMobileMenuOpen}>
                            <MenuIcon fontSize="large" style={{ color: '#4b5563' }} />
                        </div>
                    </div>
                    <div className="flex-1 flex justify-center items-center">
                        {/* Logo hidden on mobile to save space for burger menu */}
                        <div className="w-[120px] hidden sm:block">
                            <ClickableHelloDataMark />
                        </div>
                    </div>
                    <div className="w-[40px] flex justify-end items-center gap-[8px]">
                        {/* <NotificationArea /> */}
                        <SettingAvatar />
                    </div>
                </div>

                {/* Mobile Menu */}
                <Menu
                    anchorEl={mobileMenuAnchor}
                    open={open}
                    onClose={handleMobileMenuClose}
                    sx={{
                        position: 'fixed',
                        // top: '72px !important',
                        left: '0 !important',
                        right: '0 !important',
                        '& .MuiMenu-paper': {
                            width: '100vw',
                            left: '0px !important',
                            right: 0,
                            maxWidth: 'none',
                            borderRadius: 0,
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                            marginTop: 0,
                            position: 'fixed',
                            // top: '72px !important',
                        },
                        '& .MuiList-root': {
                            padding: '0px',
                        },
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    slotProps={{
                        paper: {
                            style: {
                                marginTop: 0,
                                position: 'fixed',
                                // top: '72px !important',
                            },
                        },
                    }}
                >
                    <div className="bg-white">
                        {/* Logo section */}
                        <div className="flex justify-center py-4 border-b border-gray-100">
                            <ClickableHelloDataMark />
                        </div>

                        {/* Navigation items */}
                        <div className="border-t border-gray-100">
                            {tabItems.map(({ index, label, icon, dataCy }) => (
                                <MenuItem
                                    key={index}
                                    onClick={() => handleMobileTabClick(index)}
                                    className="!py-3 !px-6 !text-base hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                                    data-cy={dataCy}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-6 h-6 flex items-center justify-center">{icon}</div>
                                        <span>{label}</span>
                                    </div>
                                </MenuItem>
                            ))}
                        </div>

                        {/* Feedback section */}
                        <div className="border-t border-gray-100">
                            <MobileFeedbackButton />
                        </div>
                    </div>
                </Menu>
            </div>
        </div>
    );
};

export default MainHedaer;
